import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { RadioButtonComponent, ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { GridComponent, ColumnsDirective, ColumnDirective } from '@syncfusion/ej2-react-grids';
import { ListBoxComponent } from '@syncfusion/ej2-react-dropdowns';

import { useWizardContext } from '../../contexts/useWizardContext';
import type {
  WizardBatchData
} from '../../contexts/ProductionScheduleWizardContext';
import { RoutingControllerService } from '../../api/services/RoutingControllerService';
import { ResourceTypeControllerService } from '../../api/services/ResourceTypeControllerService';
import type { RoutingValidationIssue } from '../../contexts/ProductionScheduleWizardContext';
import { getEffectiveMaterialId } from '../../utils/aliasProductUtils';
import { logWizardBatchQty } from '../../utils/wizardBatchQuantityDebug';

const BatchCreation: React.FC = () => {
  const { t, i18n } = useTranslation(['common']);
  
  const {
    effectiveDemands,
    materials,
    demandBucketsByProduct,
    productionDisplayDemandsAggregated,
    formData,
    batchDeterminationData,
    batchDeterminationOverrides,
    setBatchDeterminationOverride,
    calculateBatchDetermination,
    adjustBatchCountForSequence,
    sequenceEntries,
    sequenceOptimization,
    materialIdsQuantityLocked,
    lockedReplanningMaterialIds
  } = useWizardContext();

  // Store manually entered desired quantities per material
  const [manualDesiredQuantities, setManualDesiredQuantities] = useState<Record<string, number>>({});

  // When materials get quantity-locked (e.g. after the 'set quantities to zero' action), clear their manual overrides so the UI shows 0
  useEffect(() => {
    const lockedIds = Object.keys(materialIdsQuantityLocked || {}).filter(id => materialIdsQuantityLocked![id]);
    if (lockedIds.length === 0) return;
    setManualDesiredQuantities(prev => {
      let changed = false;
      const next = { ...prev };
      lockedIds.forEach(id => {
        if (id in next) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [materialIdsQuantityLocked]);
  
  // Store selected material ID (replaces tab selection)
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null);
  
  // Store routing validation issues for visual marking
  const [routingValidationIssues, setRoutingValidationIssues] = useState<Map<string, RoutingValidationIssue>>(new Map());

  // Helper function to get consistent materialId (only UUID)
  const getMaterialId = useCallback((material: any): string => {
    return material?.uuid || 'unknown';
  }, []);

  const resolveDoughBatchMethod = useCallback((materialId: string, currentData?: WizardBatchData) => {
    const override = (batchDeterminationOverrides as Record<string, any>)[materialId];
    if (override?.doughBatchMethod) {
      return override.doughBatchMethod as 'even' | 'full';
    }
    if (currentData?.doughBatchMethod) {
      return currentData.doughBatchMethod;
    }
    const material = materials.find((m: any) => m.uuid === materialId);
    return material?.doughBatchMethod === 'FULL' ? 'full' : 'even';
  }, [batchDeterminationOverrides, materials]);

  /**
   * Net piece count for the batch determination: always the sum of productionDisplayDemandsAggregated in the calendar [plannedStart, plannedEnd].
   * Same source as the production quantities tab (an order beats the forecast, an explicit 0 counts as 0).
   * demandBucketsByProduct only contains days with a quantity > 0 (for the time distribution); the overall sum must not depend on the buckets alone.
   */
  const getQuantityTotalForBatchStep = useCallback(
    (materialId: string, _groupDemands: any[] | undefined): number => {
      const ps = formData.plannedStart?.split('T')[0];
      const pe = formData.plannedEnd?.split('T')[0];
      if (!ps || !pe) return 0;
      let s = 0;
      for (const d of productionDisplayDemandsAggregated) {
        if (d.materialId !== materialId) continue;
        const dateKey = d.demandDate?.split('T')[0] || '';
        if (!dateKey || dateKey < ps || dateKey > pe) continue;
        s += d.quantity ?? 0;
      }
      return s;
    },
    [productionDisplayDemandsAggregated, formData.plannedStart, formData.plannedEnd]
  );

  const bucketSyncSigRef = useRef<Record<string, string>>({});
  const noBucketRecalcKeyRef = useRef<Record<string, string>>({});
  const prevHadManualDesiredRef = useRef<Record<string, boolean>>({});

  // Filter materials to only show PRODUCTs
  const productMaterials = useMemo(() => {
    return materials.filter((m: any) => m.materialType === 'PRODUCT');
  }, [materials]);

  // Main products only (no aliases) – used for "add empty groups" and as display material per group
  const mainProductMaterials = useMemo(() => {
    return productMaterials.filter((m: any) => !m.isAlias);
  }, [productMaterials]);

  // Group demands by effective material ID (alias → main product) so alias demands are merged with main product
  const materialGroupsArray = useMemo(() => {
    const materialGroups = effectiveDemands.reduce((acc: Record<string, { material: any; demands: any[] }>, demand: any) => {
      const effectiveId = getEffectiveMaterialId(demand.materialId, productMaterials) || demand.materialId || 'unknown';
      const mainProduct = productMaterials.find((m: any) => m.uuid === effectiveId);
      if (!mainProduct) return acc;
      if (!acc[effectiveId]) {
        acc[effectiveId] = {
          material: mainProduct,
          demands: []
        };
      }
      acc[effectiveId].demands.push(demand);
      return acc;
    }, {} as Record<string, { material: any; demands: any[] }>);

    // Add main products that have no demands (so they still appear as tabs)
    mainProductMaterials.forEach((material: any) => {
      const materialId = material.uuid || '';
      if (materialId && !materialGroups[materialId]) {
        materialGroups[materialId] = {
          material: material,
          demands: []
        };
      }
    });

    return Object.values(materialGroups) as Array<{ material: any; demands: any[] }>;
  }, [effectiveDemands, productMaterials, mainProductMaterials]);

  // Filter to only show products that have a sequence entry in Step 2 (or all main products if sequence optimization)
  const filteredMaterialGroupsArray = useMemo(() => {
    let filtered: Array<{ material: any; demands: any[] }>;
    if (sequenceOptimization) {
      // Show ALL main products, even those without demand - in step 3 the user can set a
      // production quantity manually even when there is no demand (for example pre-production).
      filtered = materialGroupsArray as Array<{ material: any; demands: any[] }>;
    } else {
      filtered = (materialGroupsArray as Array<{ material: any; demands: any[] }>).filter(
        (group: { material: any; demands: any[] }) => {
          const materialId = getMaterialId(group.material);
          return sequenceEntries.some((entry: any) => entry.productId === materialId);
        });
    }
    // Sort alphabetically by product name when sequence optimization is active.
    // In sequence entry mode the order chosen by the user is left untouched.
    if (sequenceOptimization) {
      filtered = [...filtered].sort((a, b) =>
        (a.material.name || '').localeCompare(b.material.name || '', undefined, { sensitivity: 'base' })
      );
    }
    return filtered;
  }, [materialGroupsArray, sequenceEntries, sequenceOptimization, getMaterialId]);

  // Validate routings for all products in the list (only active routings)
  useEffect(() => {
    const validateRoutings = async () => {
      if (filteredMaterialGroupsArray.length === 0) {
        setRoutingValidationIssues(new Map());
        return;
      }
      
      // Load resource types once
      const resourceTypes = await ResourceTypeControllerService.findAll();
      const resourceTypeById = new Map(resourceTypes.map(rt => [rt.uuid || '', rt]));
      
      const issuesMap = new Map<string, RoutingValidationIssue>();
      
      // Validate each product
      for (const group of filteredMaterialGroupsArray as Array<{ material: any; demands: any[] }>) {
        const materialId = getMaterialId(group.material);
        const materialName = group.material?.name || materialId;
        
        // Check for active routing (only active routings are returned)
        let activeRouting = null;
        try {
          activeRouting = await RoutingControllerService.getActiveRoutingForMaterial(materialId);
        } catch (error: any) {
          // 404 means: no active routing found - that is fine, we treat it as a missing routing
          if (error?.response?.status === 404 || error?.status === 404) {
            activeRouting = null;
          } else {
            // Rethrow other errors
            throw error;
          }
        }
        
        if (!activeRouting) {
          issuesMap.set(materialId, {
            materialId,
            materialName,
            reason: 'missingRouting'
          });
          continue;
        }
        
        // Check if routing status is COMPLETE (must be active AND complete)
        if (activeRouting.status !== 'COMPLETE') {
          issuesMap.set(materialId, {
            materialId,
            materialName,
            routingName: activeRouting.name,
            reason: 'incompleteRouting'
          });
          continue;
        }
        
        // Check if routing has processing line
        const hasProcessingLine = (activeRouting.operations || []).some(op => {
          const resourceTypeId = op.primaryResourceTypeId;
          if (!resourceTypeId) {
            return false;
          }
          const resourceType = resourceTypeById.get(resourceTypeId);
          return resourceType?.category === 'PROCESSING_LINE';
        });
        
        if (!hasProcessingLine) {
          issuesMap.set(materialId, {
            materialId,
            materialName,
            routingName: activeRouting.name,
            reason: 'missingProcessingLine'
          });
        }
      }
      
      setRoutingValidationIssues(issuesMap);
    };
    
    validateRoutings();
  }, [filteredMaterialGroupsArray, getMaterialId]);

  // Prepare data for ListBoxComponent
  const listBoxDataSource = useMemo(() => {
    return (filteredMaterialGroupsArray as Array<{ material: any; demands: any[] }>).map((group: { material: any; demands: any[] }) => {
      const materialId = getMaterialId(group.material);
      const material = group.material;
      const totalQuantity = getQuantityTotalForBatchStep(materialId, group.demands);
      const hasDemand = totalQuantity > 0;
      const routingIssue = routingValidationIssues.get(materialId);
      const hasValidRouting = !routingIssue;
      
      return {
        id: materialId,
        text: material?.name || 'Product',
        weight: material?.weight || '',
        hasDemand: hasDemand,
        hasValidRouting: hasValidRouting,
        routingIssue: routingIssue,
        materialId: materialId
      };
    });
  }, [filteredMaterialGroupsArray, getMaterialId, routingValidationIssues, getQuantityTotalForBatchStep]);

  // Auto-select first material if none selected and we have filtered materials
  useEffect(() => {
    if (!selectedMaterialId && filteredMaterialGroupsArray.length > 0) {
      const firstGroup = filteredMaterialGroupsArray[0] as { material: any; demands: any[] };
      const firstMaterialId = getMaterialId(firstGroup.material);
      setSelectedMaterialId(firstMaterialId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally depends on length only, not the full array (avoids infinite loop)
  }, [filteredMaterialGroupsArray.length, selectedMaterialId, getMaterialId]);

  // When production buckets are loaded later (for example a long-running offset): recalculate, even if wrong initial data already exists.
  useEffect(() => {
    (filteredMaterialGroupsArray as Array<{ material: any; demands: any[] }>).forEach((group: { material: any; demands: any[] }) => {
      const materialId = getMaterialId(group.material);
      const hasManual =
        manualDesiredQuantities[materialId] !== undefined && manualDesiredQuantities[materialId] !== null;
      const hadManual = prevHadManualDesiredRef.current[materialId] ?? false;
      prevHadManualDesiredRef.current[materialId] = hasManual;
      if (hasManual) {
        return;
      }
      if (hadManual && !hasManual) {
        delete bucketSyncSigRef.current[materialId];
      }
      const buckets = demandBucketsByProduct?.[materialId];
      if (!buckets?.length) {
        delete bucketSyncSigRef.current[materialId];
        const ps = formData.plannedStart?.split('T')[0] ?? '';
        const pe = formData.plannedEnd?.split('T')[0] ?? '';
        const totalQty = getQuantityTotalForBatchStep(materialId, group.demands);
        const recalcKey = `${ps}|${pe}|${totalQty}`;
        if (noBucketRecalcKeyRef.current[materialId] === recalcKey) {
          return;
        }
        noBucketRecalcKeyRef.current[materialId] = recalcKey;
        const method = resolveDoughBatchMethod(materialId);
        const scrapRate = group.material?.scrapRate ?? 0;
        const scrapQuantity = Math.round(totalQty * scrapRate);
        calculateBatchDetermination(materialId, totalQty + scrapQuantity, method);
        return;
      }
      delete noBucketRecalcKeyRef.current[materialId];
      const sig = JSON.stringify(buckets.map((b: any) => [b.demandDate, b.quantity, b.dueTime ?? '']));
      if (bucketSyncSigRef.current[materialId] === sig) {
        return;
      }
      bucketSyncSigRef.current[materialId] = sig;
      const method = resolveDoughBatchMethod(materialId);
      calculateBatchDetermination(materialId, buckets, method);
    });
  }, [
    demandBucketsByProduct,
    filteredMaterialGroupsArray,
    manualDesiredQuantities,
    calculateBatchDetermination,
    getMaterialId,
    resolveDoughBatchMethod,
    formData.plannedStart,
    formData.plannedEnd,
    getQuantityTotalForBatchStep
  ]);

  // Fallback initialization only without buckets and only when no batch data exists yet
  useEffect(() => {
    (filteredMaterialGroupsArray as Array<{ material: any; demands: any[] }>).forEach((group: { material: any; demands: any[] }) => {
      const materialId = getMaterialId(group.material);
      if (manualDesiredQuantities[materialId] !== undefined && manualDesiredQuantities[materialId] !== null) {
        return;
      }
      const buckets = demandBucketsByProduct?.[materialId];
      if (buckets?.length) {
        return;
      }
      if ((batchDeterminationData as Record<string, WizardBatchData>)[materialId]) {
        return;
      }
      const method = resolveDoughBatchMethod(materialId);
      const totalQuantity = getQuantityTotalForBatchStep(materialId, group.demands);
      const scrapRate = group.material?.scrapRate ?? 0;
      const scrapQuantity = Math.round(totalQuantity * scrapRate);
      const desiredQuantity = totalQuantity + scrapQuantity;
      calculateBatchDetermination(materialId, desiredQuantity, method);
    });
  }, [
    filteredMaterialGroupsArray,
    batchDeterminationData,
    demandBucketsByProduct,
    manualDesiredQuantities,
    calculateBatchDetermination,
    getMaterialId,
    resolveDoughBatchMethod,
    getQuantityTotalForBatchStep
  ]);

  // Dev: the product selected in the batch determination (extends context.snapshot)
  useEffect(() => {
    if (!import.meta.env.DEV || !selectedMaterialId) return;
    const group = (filteredMaterialGroupsArray as Array<{ material: any; demands: any[] }>).find(
      g => getMaterialId(g.material) === selectedMaterialId
    );
    const buckets = demandBucketsByProduct?.[selectedMaterialId];
    const batchData = (batchDeterminationData as Record<string, WizardBatchData>)[selectedMaterialId];
    const effSum = group?.demands?.reduce((s, d) => s + (d.quantity ?? 0), 0) ?? 0;
    const bucketSum = buckets?.reduce((s: number, b: any) => s + (b.quantity ?? 0), 0) ?? 0;
    const horizonProdSum = getQuantityTotalForBatchStep(selectedMaterialId, group?.demands);
    logWizardBatchQty('BatchCreation.selectedMaterial', {
      selectedMaterialId,
      materialName: group?.material?.name,
      effectiveDemandsGroupSum: effSum,
      productionSumInPlanningHorizon: horizonProdSum,
      demandBuckets: buckets ?? [],
      bucketSum,
      batchDeterminationSnapshot: batchData
        ? {
            desiredQuantity: batchData.desiredQuantity,
            bakedQuantity: batchData.bakedQuantity,
            doughBatchMethod: batchData.doughBatchMethod
          }
        : null,
      manualDesiredQuantity: manualDesiredQuantities[selectedMaterialId],
      bucketSyncSig: bucketSyncSigRef.current[selectedMaterialId] ?? null
    });
  }, [
    selectedMaterialId,
    filteredMaterialGroupsArray,
    demandBucketsByProduct,
    batchDeterminationData,
    manualDesiredQuantities,
    getMaterialId,
    getQuantityTotalForBatchStep
  ]);


  // Handle batch determination change. On a manual override take the quantity from the override, otherwise from the buckets or currentData.
  const handleBatchDeterminationChange = useCallback((materialId: string, value: string) => {
    const override = (batchDeterminationOverrides as Record<string, any>)[materialId] || { materialId };
    setBatchDeterminationOverride(materialId, {
      ...override,
      batchDetermination: value as 'FULL_OVEN' | 'OVEN_UTILIZATION'
    });

    const currentData = (batchDeterminationData as Record<string, WizardBatchData>)[materialId];
    if (currentData) {
      const method = resolveDoughBatchMethod(materialId, currentData);
      const buckets = demandBucketsByProduct?.[materialId];
      const hasManualOverride = manualDesiredQuantities[materialId] !== undefined && manualDesiredQuantities[materialId] !== null;
      if (hasManualOverride) {
        calculateBatchDetermination(materialId, manualDesiredQuantities[materialId] as number, method);
      } else if (buckets?.length > 0) {
        calculateBatchDetermination(materialId, buckets, method);
      } else {
        calculateBatchDetermination(materialId, currentData.desiredQuantity, method);
      }
    }
  }, [batchDeterminationOverrides, batchDeterminationData, demandBucketsByProduct, manualDesiredQuantities, setBatchDeterminationOverride, calculateBatchDetermination, resolveDoughBatchMethod]);

  // Handle min oven utilization change. On a manual override take the quantity from the override, otherwise from the buckets or currentData.
  const handleMinOvenUtilizationChange = useCallback((materialId: string, value: number | null) => {
    const override = (batchDeterminationOverrides as Record<string, any>)[materialId] || { materialId };
    setBatchDeterminationOverride(materialId, {
      ...override,
      minOvenUtilizationPercent: value || undefined
    });

    const currentData = (batchDeterminationData as Record<string, WizardBatchData>)[materialId];
    if (currentData) {
      const method = resolveDoughBatchMethod(materialId, currentData);
      const buckets = demandBucketsByProduct?.[materialId];
      const hasManualOverride = manualDesiredQuantities[materialId] !== undefined && manualDesiredQuantities[materialId] !== null;
      if (hasManualOverride) {
        calculateBatchDetermination(materialId, manualDesiredQuantities[materialId] as number, method);
      } else if (buckets?.length > 0) {
        calculateBatchDetermination(materialId, buckets, method);
      } else {
        calculateBatchDetermination(materialId, currentData.desiredQuantity, method);
      }
    }
  }, [batchDeterminationOverrides, batchDeterminationData, demandBucketsByProduct, manualDesiredQuantities, setBatchDeterminationOverride, calculateBatchDetermination, resolveDoughBatchMethod]);

  // Handle desired quantity change - only store the value, don't recalculate yet
  const handleDesiredQuantityChange = useCallback((materialId: string, value: number) => {
    setManualDesiredQuantities(prev => ({
      ...prev,
      [materialId]: value
    }));
    // Don't recalculate here - wait for the refresh button click
  }, []);

  // Handler for refresh - recalculates the batches for the current material.
  // With a manually overridden quantity always use that one (even for alias/buckets), otherwise the buckets or the initial quantity.
  const handleUpdate = useCallback((materialId: string) => {
    const group = (filteredMaterialGroupsArray as Array<{ material: any; demands: any[] }>).find((g: { material: any; demands: any[] }) => getMaterialId(g.material) === materialId) 
                  || (materialGroupsArray as Array<{ material: any; demands: any[] }>).find((g: { material: any; demands: any[] }) => getMaterialId(g.material) === materialId);
    if (!group) {
      console.warn('handleUpdate: Group not found for materialId:', materialId);
      return;
    }
    const totalQuantity = getQuantityTotalForBatchStep(materialId, group.demands);
    const scrapRate = group.material?.scrapRate ?? 0;
    const scrapQuantity = Math.round(totalQuantity * scrapRate);
    const initialDesiredQuantity = totalQuantity + scrapQuantity;
    const desiredQuantity = manualDesiredQuantities[materialId] ?? initialDesiredQuantity;

    const currentData = (batchDeterminationData as Record<string, WizardBatchData>)[materialId];
    const doughBatchMethod = resolveDoughBatchMethod(materialId, currentData);
    const buckets = demandBucketsByProduct?.[materialId];

    // A manual override wins: always calculate with the desired piece count, never with the buckets.
    if (manualDesiredQuantities[materialId] !== undefined && manualDesiredQuantities[materialId] !== null) {
      calculateBatchDetermination(materialId, desiredQuantity, doughBatchMethod);
    } else if (buckets?.length > 0) {
      calculateBatchDetermination(materialId, buckets, doughBatchMethod);
    } else {
      calculateBatchDetermination(materialId, desiredQuantity, doughBatchMethod);
    }
  }, [filteredMaterialGroupsArray, materialGroupsArray, manualDesiredQuantities, batchDeterminationData, demandBucketsByProduct, calculateBatchDetermination, resolveDoughBatchMethod, getMaterialId, getQuantityTotalForBatchStep]);

  // Handler for reset - restores the initial values for the current material
  const handleReset = useCallback((materialId: string) => {
    // Try filteredMaterialGroupsArray first, then fallback to materialGroupsArray
    const group = (filteredMaterialGroupsArray as Array<{ material: any; demands: any[] }>).find((g: { material: any; demands: any[] }) => getMaterialId(g.material) === materialId)
                  || (materialGroupsArray as Array<{ material: any; demands: any[] }>).find((g: { material: any; demands: any[] }) => getMaterialId(g.material) === materialId);
    if (!group) {
      console.warn('handleReset: Group not found for materialId:', materialId);
      return;
    }
    
    const totalQuantity = getQuantityTotalForBatchStep(materialId, group.demands);
    const scrapRate = group.material?.scrapRate ?? 0;
    const scrapQuantity = Math.round(totalQuantity * scrapRate);
    const initialDesiredQuantity = totalQuantity + scrapQuantity;
    
    // Reset manual quantities
    setManualDesiredQuantities(prev => {
      const updated = { ...prev };
      delete updated[materialId];
      return updated;
    });
    
    // Reset overrides
    setBatchDeterminationOverride(materialId, {
      materialId
    });
    
    const resetMethod = group.material?.doughBatchMethod === 'FULL' ? 'full' : 'even';
    const buckets = demandBucketsByProduct?.[materialId];
    if (buckets?.length > 0) {
      calculateBatchDetermination(materialId, buckets, resetMethod);
    } else {
      calculateBatchDetermination(materialId, initialDesiredQuantity, resetMethod);
    }
  }, [filteredMaterialGroupsArray, materialGroupsArray, demandBucketsByProduct, calculateBatchDetermination, setBatchDeterminationOverride, getMaterialId, getQuantityTotalForBatchStep]);

  // Get the currently selected material group
  const selectedMaterialGroup = useMemo(() => {
    if (!selectedMaterialId) return null;
    return (filteredMaterialGroupsArray as Array<{ material: any; demands: any[] }>).find((group: { material: any; demands: any[] }) => getMaterialId(group.material) === selectedMaterialId) || null;
  }, [selectedMaterialId, filteredMaterialGroupsArray, getMaterialId]);


  // Calculate values for selected material (moved out of IIFE)
  const selectedMaterialData = useMemo(() => {
    if (!selectedMaterialGroup) return null;
    
    const group = selectedMaterialGroup as { material: any; demands: any[] };
    const materialId = getMaterialId(group.material);
    const material = group.material;
    
    // Get batch determination data
    const batchData = (batchDeterminationData as Record<string, WizardBatchData>)[materialId];
    const override = (batchDeterminationOverrides as Record<string, any>)[materialId];
    
    // Get effective batch determination (override or product default)
    const effectiveBatchDetermination = override?.batchDetermination || material?.ovenBatchDetermination || material?.batchDetermination || 'FULL_OVEN';
    const effectiveMinOvenUtilization = override?.minOvenUtilizationPercent || material?.minOvenUtilizationPercent;
    
    // Base piece count: buckets or the production aggregate inside the planning horizon only (see getQuantityTotalForBatchStep)
    const totalQuantity = getQuantityTotalForBatchStep(materialId, group.demands);
    const scrapRate = material?.scrapRate ?? 0;
    const scrapQuantity = Math.round(totalQuantity * scrapRate);
    const initialDesiredQuantity = totalQuantity + scrapQuantity;
    // Use manual quantity if set, otherwise use initial from demands (0 when quantity locked)
    const isQuantityLocked = !!materialIdsQuantityLocked[materialId];
    // Replanning: first operation done -> the quantity is fixed by fixedDoughBatches and cannot be changed
    const isReplanningLocked = lockedReplanningMaterialIds?.has(materialId) ?? false;
    const isLocked = isQuantityLocked || isReplanningLocked;
    const desiredQuantity = isQuantityLocked ? 0 : (manualDesiredQuantities[materialId] ?? initialDesiredQuantity);

    return {
      group,
      materialId,
      material,
      batchData,
      override,
      effectiveBatchDetermination,
      effectiveMinOvenUtilization,
      totalQuantity,
      scrapQuantity,
      initialDesiredQuantity,
      desiredQuantity,
      isQuantityLocked,
      isReplanningLocked,
      isLocked,
    };
  }, [selectedMaterialGroup, batchDeterminationData, batchDeterminationOverrides, manualDesiredQuantities, getMaterialId, materialIdsQuantityLocked, lockedReplanningMaterialIds, getQuantityTotalForBatchStep]);

  return (
    <div className="p-4 flex-1 min-h-0 flex flex-col">
      <div className="flex gap-4 items-stretch flex-1 min-h-0">
        {/* Left side: Product selection list */}
        <div className="w-64 flex-shrink-0 border border-gray-200 rounded-lg overflow-hidden bg-white flex flex-col min-h-0">
          {listBoxDataSource.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">
              {t('productionSchedule.wizard.step3.noProductsInSequence', 'Keine Produkte in Sequenz')}
            </div>
          ) : (
            <ListBoxComponent
              dataSource={listBoxDataSource as any}
              fields={{ text: 'text', value: 'id' }}
              value={selectedMaterialId ? [selectedMaterialId] : []}
              change={(e: any) => {
                // For ListBoxComponent, the value is an array in Single mode
                const selectedValue = Array.isArray(e.value) && e.value.length > 0 ? e.value[0] : e.value;

                // Also try to get from itemData
                const materialId = e.itemData?.materialId ||
                                   e.itemData?.id ||
                                   selectedValue;

                if (materialId) {
                  setSelectedMaterialId(materialId);
                } else {
                  console.warn('Could not find materialId in event:', e);
                }
              }}
              height="100%"
              cssClass="product-selection-listbox"
              selectionSettings={{ mode: 'Single' }}
              itemTemplate={(data: { text: string; weight?: string; hasValidRouting: boolean; hasDemand: boolean; routingIssue?: RoutingValidationIssue }) => {
                const routingIssue = data.routingIssue;
                const tooltipText = routingIssue 
                  ? (routingIssue.reason === 'missingRouting'
                      ? t('productionSchedule.wizard.step3.validation.noRouting', 'Kein aktiver Arbeitsplan vorhanden')
                      : routingIssue.reason === 'incompleteRouting'
                      ? t('productionSchedule.wizard.step3.validation.incompleteRouting', 'Arbeitsplan ist nicht vollständig')
                      : t('productionSchedule.wizard.step3.validation.missingProcessingLine', 'Arbeitsplan ohne Verarbeitungslinie'))
                  : '';
                
                return (
                  <div className="flex items-center justify-between p-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {data.text}
                      </div>
                      {data.weight && (
                        <div className="text-xs text-gray-500 mt-1">
                          {data.weight}
                        </div>
                      )}
                    </div>
                    <div className="ml-2 flex-shrink-0 flex items-center gap-1">
                      {!data.hasValidRouting && (
                        <span 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" 
                          title={tooltipText}
                        >
                          ⚠
                        </span>
                      )}
                      {!data.hasDemand && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800" title={t('productionSchedule.wizard.step3.noDemandTooltip', 'Kein Bedarf')}>
                          !
                        </span>
                      )}
                    </div>
                  </div>
                );
              }}
            />
          )}
        </div>

        {/* Right side: Content area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
            {selectedMaterialData ? (() => {
              const { group: _group, materialId, material: _material, batchData, effectiveBatchDetermination, effectiveMinOvenUtilization, totalQuantity, scrapQuantity, desiredQuantity, isLocked, isReplanningLocked } = selectedMaterialData;

              return (
                <div className="pb-4 pl-4 pr-4 flex flex-col" key={materialId}>
                  {/* 1. Initial quantity */}
                  <div className="mb-4 flex-shrink-0">
                    <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-sm font-semibold text-blue-900">
                        {t('productionSchedule.wizard.step3.desiredQuantityHint', 'Initial: {{total}} (Produktionsmenge) + {{scrap}} (Ausschuß)', {
                          total: totalQuantity,
                          scrap: scrapQuantity
                        })}
                      </p>
                    </div>
                  </div>

                  {/* 2. Radio buttons: dough batch method and batch determination type */}
                  <div className="mb-4 flex-shrink-0">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Left: dough batch formation */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('productionSchedule.wizard.step3.doughBatchMethodLabel', 'Teig-Chargen-Bildung')}
                        </label>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <RadioButtonComponent
                              name={`doughBatchMethod_${materialId}`}
                              value="even"
                              checked={resolveDoughBatchMethod(materialId, batchData) === 'even'}
                              change={(_e: any) => {
                                const override = (batchDeterminationOverrides as Record<string, any>)[materialId] || { materialId };
                                setBatchDeterminationOverride(materialId, { ...override, doughBatchMethod: 'even' });
                                if (batchData) {
                                  calculateBatchDetermination(materialId, desiredQuantity, 'even');
                                }
                              }}
                              label={t('productionSchedule.wizard.step3.doughBatchMethod.even', 'Gleichmäßige Verteilung')}
                              cssClass="mr-2"
                            />
                          </div>
                          <div className="flex items-center">
                            <RadioButtonComponent
                              name={`doughBatchMethod_${materialId}`}
                              value="full"
                              checked={resolveDoughBatchMethod(materialId, batchData) === 'full'}
                              change={(_e: any) => {
                                const override = (batchDeterminationOverrides as Record<string, any>)[materialId] || { materialId };
                                setBatchDeterminationOverride(materialId, { ...override, doughBatchMethod: 'full' });
                                if (batchData) {
                                  calculateBatchDetermination(materialId, desiredQuantity, 'full');
                                }
                              }}
                              label={t('productionSchedule.wizard.step3.doughBatchMethod.full', 'Volle Mengen')}
                              cssClass="mr-2"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Right: piece count calculation */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('product.batchDeterminationLabel', 'Stückzahlberechnung')}
                        </label>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <RadioButtonComponent
                              name={`batchDetermination_${materialId}`}
                              value="FULL_OVEN"
                              checked={effectiveBatchDetermination === 'FULL_OVEN'}
                              change={(e: any) => {
                                handleBatchDeterminationChange(materialId, e.value);
                              }}
                              label={t('product.batchDeterminationOptions.FULL_OVEN', 'Volle Öfen')}
                              cssClass="mr-2"
                            />
                            <div className="w-32"></div>
                          </div>
                          <div className="flex items-center justify-between">
                            <RadioButtonComponent
                              name={`batchDetermination_${materialId}`}
                              value="OVEN_UTILIZATION"
                              checked={effectiveBatchDetermination === 'OVEN_UTILIZATION'}
                              change={(e: any) => {
                                handleBatchDeterminationChange(materialId, e.value);
                              }}
                              label={t('product.batchDeterminationOptions.OVEN_UTILIZATION', 'Ofenauslastung')}
                              cssClass="mr-2"
                            />
                            <div className="flex items-center gap-2">
                              {effectiveBatchDetermination === 'OVEN_UTILIZATION' ? (
                                <>
                                  <NumericTextBoxComponent
                                    value={effectiveMinOvenUtilization || undefined}
                                    min={0.1}
                                    max={100}
                                    step={0.1}
                                    format="n1"
                                    change={(e: any) => handleMinOvenUtilizationChange(materialId, e.value)}
                                    cssClass="w-24"
                                  />
                                  <span className="text-sm text-gray-600">%</span>
                                </>
                              ) : (
                                <>
                                  <div className="w-24"></div>
                                  <span className="text-sm text-gray-400 w-4"></span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Desired piece count with buttons */}
                  <div className="mb-4 flex-shrink-0">
                    <div className="flex items-end gap-2">
                      <div className="w-48">
                        <div className="flex items-center gap-2 mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('productionSchedule.wizard.step3.desiredQuantity', 'Gewünschte Stückzahl')}
                          </label>
                          {(() => {
                            // Check if product is in sequence
                            const isInSequence = sequenceEntries.some((entry: any) => entry.productId === materialId);
                            if (!isInSequence) {
                              return (
                                <div className="group relative">
                                  <svg className="h-5 w-5 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                  </svg>
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                    <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                      {t('productionSchedule.wizard.step3.notInSequenceTooltip', 'Produkt ist nicht Teil der Sequenz')}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <NumericTextBoxComponent
                          value={desiredQuantity}
                          min={0}
                          step={1}
                          format="n0"
                          change={(e: any) => !isLocked && handleDesiredQuantityChange(materialId, e.value || 0)}
                          enabled={!isLocked && sequenceEntries.some((entry: any) => entry.productId === materialId)}
                        />
                      </div>
                      <div className="flex gap-2">
                        {isReplanningLocked && (
                          <span className="text-xs text-blue-700 self-center">
                            {t('productionSchedule.wizard.step3.validation.quantityLockedReplanning', 'Menge fixiert – erste Operation abgeschlossen')}
                          </span>
                        )}
                        {!isReplanningLocked && isLocked && (
                          <span className="text-xs text-amber-700 self-center">
                            {t('productionSchedule.wizard.step3.validation.quantityLockedNoRouting', 'Kein Arbeitsplan – Menge auf 0 gesetzt und gesperrt')}
                          </span>
                        )}
                        <ButtonComponent
                          type="button"
                          disabled={isLocked}
                          ref={(btn: any) => {
                            if (btn && btn.element && materialId) {
                              btn.element.onclick = () => {
                                if (!isLocked) handleUpdate(materialId);
                              };
                            }
                          }}
                          cssClass="e-outline"
                        >
                          {t('actions.update', 'Aktualisieren')}
                        </ButtonComponent>
                        <ButtonComponent
                          type="button"
                          disabled={isLocked}
                          ref={(btn: any) => {
                            if (btn && btn.element && materialId) {
                              btn.element.onclick = () => {
                                if (!isLocked) handleReset(materialId);
                              };
                            }
                          }}
                          cssClass="e-outline"
                        >
                          {t('actions.reset', 'Zurücksetzen')}
                        </ButtonComponent>
                      </div>
                    </div>
                  </div>

                  {/* Warning about unassigned batches */}
                  {batchData?.assignmentWarning && (
                    <div className="mb-4 flex-shrink-0 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-yellow-900 mb-2">
                            {t('productionSchedule.wizard.step3.assignmentWarning.title', 'Warnung: Nicht alle Chargen konnten zugeordnet werden')}
                          </div>
                          <div className="text-sm text-yellow-800 mb-2">
                            {t('productionSchedule.wizard.step3.assignmentWarning.message', 
                              '{{unassigned}} von {{total}} Chargen konnten nicht den Sequenzeinträgen zugeordnet werden. Die Sequenz erlaubt maximal {{maxBatches}} Chargen.',
                              {
                                unassigned: batchData.assignmentWarning.unassignedBatches,
                                total: batchData.assignmentWarning.totalBatches,
                                maxBatches: batchData.assignmentWarning.maxBatchesSum || 'unbegrenzt'
                              }
                            )}
                          </div>
                          <div className="text-xs text-yellow-700">
                            {t('productionSchedule.wizard.step3.assignmentWarning.hint', 
                              'Hinweis: Sie können die Sequenz anpassen (maxBatches erhöhen) oder die Chargenanzahl automatisch anpassen lassen.')
                            }
                          </div>
                        </div>
                        <ButtonComponent
                          ref={(btn: any) => {
                            if (btn && btn.element && materialId && adjustBatchCountForSequence) {
                              btn.element.onclick = () => {
                                adjustBatchCountForSequence(materialId);
                              };
                            }
                          }}
                          cssClass="e-primary ml-4"
                        >
                          {t('productionSchedule.wizard.step3.assignmentWarning.autoFix', 'Automatisch anpassen')}
                        </ButtonComponent>
                      </div>
                    </div>
                  )}

                  {/* Calculated values (read-only, informational) */}
                  {batchData && (
                    <div className="mb-4 flex-shrink-0 grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="text-sm font-medium text-blue-900 mb-1">
                          {t('productionSchedule.wizard.step3.bakedQuantity', 'Zu backende Stückzahl')}
                        </div>
                        <div className="text-lg font-semibold text-blue-700">
                          {batchData.bakedQuantity}
                        </div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="text-sm font-medium text-green-900 mb-1">
                          {t('productionSchedule.wizard.step3.doughQuantity', 'Teigmenge (kg)')}
                        </div>
                        <div className="text-lg font-semibold text-green-700">
                          {batchData.doughQuantity.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Two grids side by side */}
                  {batchData && (
                    <div className="grid grid-cols-2 gap-4" style={{ minHeight: '320px' }}>
                      {/* Left: dough batch grid */}
                      <div className="flex flex-col">
                        <h4 className="text-md font-semibold mb-2 flex-shrink-0">
                          {t('productionSchedule.wizard.step3.doughBatches', 'Teig-Chargen')}
                        </h4>
                        <div style={{ height: '300px' }}>
                          <GridComponent
                            dataSource={batchData.doughBatches || []}
                            height="100%"
                            allowSorting={true}
                            allowFiltering={false}
                            locale={i18n.language === 'de' ? 'de-DE' : 'en'}
                          >
                            <ColumnsDirective>
                              <ColumnDirective
                                field="sequenceNumber"
                                headerText={t('productionSchedule.wizard.step3.sequenceNumber', 'Nr.')}
                                width={60}
                                textAlign="Right"
                              />
                              <ColumnDirective
                                field="quantity"
                                headerText={t('productionSchedule.wizard.step3.quantity', 'Stückzahl')}
                                width={100}
                                textAlign="Right"
                                format="n0"
                              />
                              <ColumnDirective
                                field="doughQuantityKg"
                                headerText={t('productionSchedule.wizard.step3.weight', 'Gewicht (kg)')}
                                width={120}
                                textAlign="Right"
                                format="n2"
                              />
                              <ColumnDirective
                                field="doughQuantityKg"
                                headerText={t('productionSchedule.wizard.step3.doughQuantity', 'Teig-Menge (kg)')}
                                width={140}
                                textAlign="Right"
                                format="n2"
                              />
                            </ColumnsDirective>
                          </GridComponent>
                        </div>
                      </div>

                      {/* Right: oven batch grid */}
                      <div className="flex flex-col">
                        <h4 className="text-md font-semibold mb-2 flex-shrink-0">
                          {t('productionSchedule.wizard.step3.ovenBatchesTitle', 'Ofen-Chargen')}
                        </h4>
                        <div style={{ height: '300px' }}>
                          <GridComponent
                            dataSource={batchData.ovenBatches || []}
                            height="100%"
                            allowSorting={true}
                            allowFiltering={false}
                            locale={i18n.language === 'de' ? 'de-DE' : 'en'}
                          >
                            <ColumnsDirective>
                              <ColumnDirective
                                field="ovenNumber"
                                headerText={t('productionSchedule.wizard.step3.ovenNumber', 'Nr.')}
                                width={60}
                                textAlign="Right"
                              />
                              <ColumnDirective
                                field="utilization"
                                headerText={t('productionSchedule.wizard.step3.utilization', 'Auslastung (%)')}
                                width={120}
                                textAlign="Right"
                                template={(props: any) => {
                                  const item = props.data || props.rowData || props;
                                  return <span>{(item.utilization * 100).toFixed(1)}%</span>;
                                }}
                              />
                              <ColumnDirective
                                field="pieces"
                                headerText={t('productionSchedule.wizard.step3.pieces', 'Stückzahl')}
                                width={100}
                                textAlign="Right"
                                format="n0"
                              />
                              <ColumnDirective
                                field="piecesPerOven"
                                headerText={t('productionSchedule.wizard.step3.piecesPerOven', 'Ofen-Kapazität')}
                                width={120}
                                textAlign="Right"
                                format="n0"
                              />
                            </ColumnsDirective>
                          </GridComponent>
                        </div>
                      </div>
                    </div>
                  )}

                  {!batchData && (
                    <div className="text-center text-gray-500 py-8">
                      {t('productionSchedule.wizard.step3.noData', 'Keine Daten verfügbar')}
                    </div>
                  )}
                </div>
              );
            })() : (
              <div className="p-8 text-center text-gray-500">
                {t('productionSchedule.wizard.step3.selectProduct', 'Bitte wählen Sie ein Produkt aus')}
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default BatchCreation;
